package er.prateek.crossly.persistence

import com.russhwolf.settings.Settings
import er.prateek.crossly.config.configKey
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.game.crossword.KeyboardMode
import er.prateek.crossly.game.crossword.StarterAmount

// Pre-per-level keys: one global value each. Still read (never written) so an upgrade carries
// the player's choice into every tier instead of resetting it.
private const val LEGACY_KEY_KEYBOARD_MODE = "gameplay_keyboard_mode"
private const val LEGACY_KEY_STARTER_AMOUNT = "gameplay_starter_amount"
private const val KEY_CLUE_GUTTER = "gameplay_clue_gutter"

private fun keyboardKey(tier: DifficultyTier) = "gameplay_keyboard_mode_${tier.configKey}"
private fun starterKey(tier: DifficultyTier) = "gameplay_starter_amount_${tier.configKey}"
private fun sourceKey(valueKey: String) = "${valueKey}_source"

class SettingsGameplayPrefsStore(
    private val settings: Settings,
) : GameplayPrefsStore {
    override fun load(): GameplayPreferences {
        val legacyKeyboard = settings.readEnum<KeyboardMode>(LEGACY_KEY_KEYBOARD_MODE)
        val legacyStarter = settings.readEnum<StarterAmount>(LEGACY_KEY_STARTER_AMOUNT)
        return GameplayPreferences(
            keyboardModes = DifficultyTier.entries.mapNotNull { tier ->
                val choice = settings.readChoice<KeyboardMode>(keyboardKey(tier))
                    ?: legacyKeyboard?.let { legacyChoice(it, LEGACY_DEFAULT_KEYBOARD_MODE) }
                choice?.let { tier to it }
            }.toMap(),
            starterAmounts = DifficultyTier.entries.mapNotNull { tier ->
                val choice = settings.readChoice<StarterAmount>(starterKey(tier))
                    ?: legacyStarter?.let { legacyChoice(it, LEGACY_DEFAULT_STARTER_AMOUNT) }
                choice?.let { tier to it }
            }.toMap(),
            showClueGutter = settings.getBoolean(KEY_CLUE_GUTTER, true),
        )
    }

    override fun save(preferences: GameplayPreferences) {
        preferences.keyboardModes.forEach { (tier, choice) -> settings.writeChoice(keyboardKey(tier), choice) }
        preferences.starterAmounts.forEach { (tier, choice) -> settings.writeChoice(starterKey(tier), choice) }
        settings.putBoolean(KEY_CLUE_GUTTER, preferences.showClueGutter)
    }
}

// Stored names are read defensively: a value written by a newer build (or a renamed enum
// entry) reads as absent instead of crashing.
private inline fun <reified T : Enum<T>> Settings.readEnum(key: String): T? =
    getStringOrNull(key)?.let { name -> enumValues<T>().firstOrNull { it.name == name } }

private inline fun <reified T : Enum<T>> Settings.readChoice(key: String): StoredChoice<T>? {
    val value = readEnum<T>(key) ?: return null
    // A value with no source was written by a build that didn't track one; treat it as the
    // player's so config never overwrites it.
    val source = readEnum<ChoiceSource>(sourceKey(key)) ?: ChoiceSource.USER
    return StoredChoice(value, source)
}

private fun <T : Enum<T>> Settings.writeChoice(key: String, choice: StoredChoice<T>) {
    putString(key, choice.value.name)
    putString(sourceKey(key), choice.source.name)
}
